import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCursor } from '../context/CursorContext';
import { AnimatedSection } from '../components/AnimatedSection';
import { subscriberOptions, budgetOptions, campaignTypes } from '../data/mock';

const API = process.env.REACT_APP_BACKEND_URL;

const inputClasses =
  'w-full bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-green-500 focus:outline-none transition-colors duration-300 placeholder:text-white/30';
const selectClasses =
  'w-full bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-green-500 focus:outline-none transition-colors duration-300 appearance-none cursor-pointer';

export default function GetStartedPage() {
  const { setLabel } = useCursor();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'creator' ? 'creator' : 'brand';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'creator') setActiveTab('creator');
  }, [searchParams]);

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-2xl mx-auto">
        <AnimatedSection className="text-center mb-10">
          <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">Get Started</p>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-3">LET'S WORK TOGETHER</h1>
          <p className="text-white/40 text-sm">Choose your path below.</p>
        </AnimatedSection>

        {/* Tab Toggle */}
        <div className="flex mb-10 border border-white/10 p-1" data-testid="get-started-tabs">
          <button
            onClick={() => setActiveTab('brand')}
            data-testid="tab-brand"
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
              activeTab === 'brand'
                ? 'bg-green-500 text-black'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            I'm a Brand
          </button>
          <button
            onClick={() => setActiveTab('creator')}
            data-testid="tab-creator"
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
              activeTab === 'creator'
                ? 'bg-green-500 text-black'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            I'm a Creator
          </button>
        </div>

        {activeTab === 'brand' ? (
          <BrandForm setLabel={setLabel} />
        ) : (
          <CreatorForm setLabel={setLabel} />
        )}
      </div>
    </div>
  );
}

function BrandForm({ setLabel }) {
  const [form, setForm] = useState({
    brand_name: '', contact_name: '', email: '', website_url: '',
    budget_range: '', campaign_type: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      // X (Twitter) Pixel conversion event — Brand Inquiry
      if (typeof window !== 'undefined' && typeof window.twq === 'function') {
        window.twq('event', 'tw-rcasv-BrandInquiry', {
          conversion_id: form.email || undefined,
          email_address: form.email || undefined,
        });
      }
      toast.success("Inquiry sent! We'll get back to you with a proposal.");
      setForm({ brand_name: '', contact_name: '', email: '', website_url: '', budget_range: '', campaign_type: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatedSection delay={100}>
      <div className="mb-6">
        <h2 className="text-2xl font-black text-white mb-2">Market Your Brand With Top Crypto Creators</h2>
        <p className="text-white/40 text-sm">Tell us about your project and we'll match you with the right creators.</p>
      </div>
      <form onSubmit={handleSubmit} className="p-8 border border-white/10 bg-black/40 relative overflow-hidden" data-testid="brand-inquiry-form">
        <div className="absolute top-0 left-0 right-0 h-px shimmer-border" />
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Brand / Project Name *</label>
            <input type="text" name="brand_name" value={form.brand_name} onChange={handleChange} placeholder="Your brand name" required className={inputClasses} data-testid="brand-name-input" />
          </div>
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Contact Name *</label>
            <input type="text" name="contact_name" value={form.contact_name} onChange={handleChange} placeholder="Your name" required className={inputClasses} data-testid="contact-name-input" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Email *</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@company.com" required className={inputClasses} data-testid="brand-email-input" />
          </div>
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Website URL</label>
            <input type="url" name="website_url" value={form.website_url} onChange={handleChange} placeholder="https://yourproject.com" className={inputClasses} data-testid="brand-website-input" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Budget Range</label>
            <select name="budget_range" value={form.budget_range} onChange={handleChange} className={selectClasses} data-testid="brand-budget-select">
              <option value="" className="bg-black">Select budget</option>
              {budgetOptions.map((opt) => (
                <option key={opt} value={opt} className="bg-black">{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Campaign Type</label>
            <select name="campaign_type" value={form.campaign_type} onChange={handleChange} className={selectClasses} data-testid="brand-campaign-select">
              <option value="" className="bg-black">Select type</option>
              {campaignTypes.map((opt) => (
                <option key={opt} value={opt} className="bg-black">{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mb-6">
          <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Tell Us About Your Project *</label>
          <textarea name="message" value={form.message} onChange={handleChange} placeholder="Describe your project, goals, and target audience..." required className={`${inputClasses} h-28 resize-none`} data-testid="brand-message-input" />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-brand-inquiry-btn"
          className="group relative w-full py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden disabled:opacity-50"
          onMouseEnter={() => setLabel('Send')}
          onMouseLeave={() => setLabel('')}
        >
          <span className="relative z-10">{submitting ? 'Sending...' : 'Get a Proposal'}</span>
          <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </button>
      </form>
      <p className="text-center text-white/20 text-xs mt-4">
        Or email us directly at{' '}
        <a href="mailto:brands@themoonmedia.com" className="text-green-500 hover:text-green-400 transition-colors">brands@themoonmedia.com</a>
      </p>
    </AnimatedSection>
  );
}

function CreatorForm({ setLabel }) {
  const [form, setForm] = useState({ name: '', email: '', channelUrl: '', subscribers: '', about: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          channel_url: form.channelUrl,
          subscribers: form.subscribers,
          about: form.about,
        }),
      });
      if (!res.ok) throw new Error();
      // X (Twitter) Pixel conversion event — Creator Application
      if (typeof window !== 'undefined' && typeof window.twq === 'function') {
        window.twq('event', 'tw-rcasv-CreatorApply', {
          conversion_id: form.email || undefined,
          email_address: form.email || undefined,
        });
      }
      toast.success("Application submitted! We'll be in touch.");
      setForm({ name: '', email: '', channelUrl: '', subscribers: '', about: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatedSection delay={100}>
      <div className="mb-6">
        <h2 className="text-2xl font-black text-white mb-2">Join The Moon Media Creator Network</h2>
        <p className="text-white/40 text-sm">Apply to join our elite network of crypto creators.</p>
      </div>
      <form onSubmit={handleSubmit} className="p-8 border border-white/10 bg-black/40 relative overflow-hidden" data-testid="creator-application-form">
        <div className="absolute top-0 left-0 right-0 h-px shimmer-border" />
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Your Name *</label>
            <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Full name" required className={inputClasses} data-testid="creator-name-input" />
          </div>
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Email *</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" required className={inputClasses} data-testid="creator-email-input" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Channel URL *</label>
            <input type="url" name="channelUrl" value={form.channelUrl} onChange={handleChange} placeholder="https://youtube.com/@yourchannel" required className={inputClasses} data-testid="creator-channel-input" />
          </div>
          <div>
            <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Subscribers</label>
            <select name="subscribers" value={form.subscribers} onChange={handleChange} className={selectClasses} data-testid="creator-subs-select">
              <option value="" className="bg-black">Select range</option>
              {subscriberOptions.map((opt) => (
                <option key={opt} value={opt} className="bg-black">{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mb-6">
          <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">About You</label>
          <textarea name="about" value={form.about} onChange={handleChange} placeholder="Tell us about your content and what you're looking for..." className={`${inputClasses} h-24 resize-none`} data-testid="creator-about-input" />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-creator-application-btn"
          className="group relative w-full py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden disabled:opacity-50"
          onMouseEnter={() => setLabel('Submit')}
          onMouseLeave={() => setLabel('')}
        >
          <span className="relative z-10">{submitting ? 'Submitting...' : 'Submit Application'}</span>
          <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </button>
      </form>
    </AnimatedSection>
  );
}
